import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { Drawer } from "@/components/ui/Drawer";
import { Icon } from "@/components/ui/Icon";
import { Pager, usePager } from "@/components/ui/Pager";
import { ErrorState } from "@/components/ui/States";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { SegmentedFilter, Toolbar } from "@/components/ui/Toolbar";
import { usePeople } from "@/lib/api/people";
import { useItem, useList } from "@/lib/api/queries";
import { dateTime, duration, qty, relative, text } from "@/lib/format";
import { roleLabel, statusLabel } from "@/lib/labels";
import "./tasks.css";

/**
 * Topshiriqlar tarixi: kim, qachon, nimani bajardi.
 *
 * Navbat ekrani (`Tasks`) faqat OCHIQ ishni ko'rsatadi — topshiriq
 * yopilishi bilan u yerdan yo'qoladi. Dispetcherga esa keyin ham
 * savol beriladi: «kechagi ЗК-003412 ni kim terdi, qancha vaqt
 * ketdi, nega kam chiqdi». Javob shu yerda.
 *
 * Manba — task-service (`GET /tasks/finished`), hisobot proyeksiyasi
 * EMAS: proyeksiya manbadan orqada qolishi mumkin va `report.read`
 * talab qiladi, tarix esa navbat bilan bir xil `task.read` bilan
 * ochiladi. Filtrlar SERVERDA: ro'yxat sahifalanadi va brauzerdagi
 * filtr faqat ochiq sahifani ko'rardi.
 */

interface FinishedTask {
  id: string;
  task_type: string;
  status: string;
  required_role: string;
  warehouse_code?: string;
  assignee_id?: string;
  source_process?: string;
  order_id?: string;
  document_number?: string;
  reason?: string;
  has_shortage: boolean;
  accepted_at?: string;
  started_at?: string;
  completed_at?: string;
  cancelled_at?: string;
  finished_at: string;
  line_count: number;
  planned_qty: string;
  actual_qty: string;
  short_lines: number;
  mismatch_lines: number;
  created_at: string;
}

interface TaskLine {
  id: string;
  line_number: number;
  sku_id: string;
  planned_qty: string;
  actual_qty?: string;
  source_location_id?: string;
  target_location_id?: string;
  status: string;
  discrepancy_reason?: string;
  confirmed_at?: string;
  confirmed_by?: string;
}

/** `GET /tasks/{id}` — ro'yxatdagidan farqi: satrlar bor, yig'indi yo'q. */
type TaskDetail = Omit<FinishedTask, "finished_at" | "line_count" | "planned_qty" | "actual_qty" | "short_lines" | "mismatch_lines"> & {
  lines?: TaskLine[];
};

interface HistoryEntry {
  from_status?: string;
  to_status: string;
  reason?: string;
  actor_id?: string;
  device_id?: string;
  occurred_at: string;
}

interface User {
  id: string;
  username: string;
  full_name: string;
  is_active: boolean;
}

type Scope = "COMPLETED" | "CANCELLED" | "ALL";

const PAGE_SIZE = 50;

/**
 * Tur ro'yxati — task-service `task_type_check` bilan bir xil.
 *
 * Qo'lda yozilgan: tarixda tur bo'yicha sanoq yo'q (navbatdagi
 * chiplar ochiq ishni sanaydi), yopilgan ish esa istalgan turda
 * bo'lishi mumkin.
 */
const TASK_TYPES = [
  "PICK", "PUTAWAY", "RECEIPT", "CONTROL", "PALLET_LOWER", "PALLET_LIFT",
  "COUNT", "REPLENISHMENT", "SHIPMENT", "MOVE", "RETURN_TO_MX", "REPACK",
  "B_TO_A_TRANSFER", "PRODUCTION",
];

/**
 * Qisqa ID — OXIRGI sakkiz belgi.
 *
 * ID UUIDv7: boshi vaqt belgisi va bir daqiqada yaratilgan
 * topshiriqlarda BIR XIL chiqadi. Oxiri tasodifiy — ro'yxatda
 * ajralib turadi va server qidiruvi uni ham topadi.
 */
const shortTaskId = (id: string) => id.slice(-8);

/** Topshiriq ustida ishlangan vaqt: boshlangandan tugaguncha. */
function workSeconds(row: { accepted_at?: string; started_at?: string; finished_at?: string }): number | null {
  const start = row.started_at ?? row.accepted_at;
  if (!start || !row.finished_at) return null;
  const seconds = (new Date(row.finished_at).getTime() - new Date(start).getTime()) / 1000;
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

/** `YYYY-MM-DD` (mahalliy kun) → kun boshi yoki oxiri, RFC3339. */
function dayBound(day: string, end: boolean): string | undefined {
  if (!day) return undefined;
  const date = new Date(`${day}T00:00:00`);
  if (Number.isNaN(date.getTime())) return undefined;
  if (end) {
    date.setDate(date.getDate() + 1);
    date.setMilliseconds(-1);
  }
  return date.toISOString();
}

/** Ism: to'liq ism, bo'lmasa login, topilmasa qisqa ID. */
function personName(names: Map<string, string>, id: string | undefined): string {
  if (!id) return "—";
  return names.get(id) ?? id.slice(0, 8);
}

export function TaskHistory() {
  const [scope, setScope] = useState<Scope>("COMPLETED");
  const [type, setType] = useState("");
  const [worker, setWorker] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [search, setSearch] = useState("");
  const [opened, setOpened] = useState<FinishedTask | null>(null);

  // Qidiruv kechiktirib yuboriladi: har bosilgan harfga so'rov
  // ketmasin.
  const [needle, setNeedle] = useState("");
  useEffect(() => {
    const timer = window.setTimeout(() => setNeedle(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  // Har qanday filtr o'zgarsa birinchi sahifaga qaytiladi.
  const pager = usePager([scope, type, worker, from, to, needle].join("|"));
  const history = useList<FinishedTask>("task", "/api/v1/tasks/finished", {
    limit: PAGE_SIZE,
    cursor: pager.cursor,
    status: scope === "ALL" ? undefined : scope,
    task_type: type || undefined,
    assignee_id: worker || undefined,
    from: dayBound(from, false),
    to: dayBound(to, true),
    q: needle || undefined,
  });
  const rows = history.data?.items ?? [];

  // Xodim tanlovi uchun to'liq ro'yxat; jadvaldagi ismlar esa
  // aniq ID lar bo'yicha — ro'yxat 500 tadan oshsa ham topiladi.
  const staff = useList<User>("identity", "/api/v1/users", { limit: 500 });
  const people = useMemo(
    () =>
      [...(staff.data?.items ?? [])].sort((left, right) =>
        (left.full_name || left.username).localeCompare(right.full_name || right.username),
      ),
    [staff.data],
  );
  const names = usePeople(rows.map((row) => row.assignee_id));

  const filtered = Boolean(type || worker || from || to || needle);

  const columns: Array<Column<FinishedTask>> = [
    {
      key: "document",
      header: "Hujjat",
      value: (row) => row.document_number ?? shortTaskId(row.id),
      render: (row) =>
        row.document_number ? (
          <span className="mono">{row.document_number}</span>
        ) : (
          <span className="mono" title={row.id}>
            #{shortTaskId(row.id)}
          </span>
        ),
    },
    {
      key: "type",
      header: "Turi",
      render: (row) => statusLabel(row.task_type),
    },
    {
      key: "status",
      header: "Holat",
      render: (row) => (
        <span title={row.reason}>
          <StatusBadge value={row.status} />
        </span>
      ),
    },
    {
      key: "worker",
      header: "Ijrochi",
      render: (row) =>
        row.assignee_id ? (
          personName(names, row.assignee_id)
        ) : (
          <span className="cell-sub">{roleLabel(row.required_role)} · olinmagan</span>
        ),
    },
    {
      key: "created",
      header: "Yaratildi",
      value: (row) => row.created_at,
      render: (row) => dateTime(row.created_at),
    },
    {
      key: "finished",
      header: "Tugadi",
      value: (row) => row.finished_at,
      render: (row) => (
        <span title={relative(row.finished_at)}>{dateTime(row.finished_at)}</span>
      ),
    },
    {
      key: "duration",
      header: "Davomiyligi",
      align: "right",
      value: (row) => workSeconds(row),
      render: (row) => duration(workSeconds(row)),
    },
    {
      key: "lines",
      header: "SKU",
      align: "right",
      value: (row) => row.line_count,
      render: (row) => <span className="num">{row.line_count}</span>,
    },
    {
      key: "planned",
      header: "Reja",
      align: "right",
      value: (row) => Number(row.planned_qty),
      render: (row) => <span className="num">{qty(row.planned_qty)}</span>,
    },
    {
      key: "actual",
      header: "Fakt",
      align: "right",
      value: (row) => Number(row.actual_qty),
      render: (row) => {
        // Bekor qilinganda fakt yo'q — «0» rejadan kam degan
        // noto'g'ri taassurot qoldirardi.
        if (row.status === "CANCELLED" && Number(row.actual_qty) === 0) return "—";
        const short = Number(row.actual_qty) < Number(row.planned_qty);
        return (
          <span className="num" style={short ? { color: "var(--error)", fontWeight: 600 } : undefined}>
            {qty(row.actual_qty)}
          </span>
        );
      },
    },
    {
      key: "discrepancy",
      header: "Farq",
      render: (row) => <DiscrepancyBadge row={row} />,
    },
  ];

  return (
    <>
      <PageHeader
        title="Topshiriqlar tarixi"
        hint="Yakunlangan va bekor qilingan topshiriqlar: kim bajardi, qancha vaqt ketdi, reja va fakt. Qatorni bosing — satrlar va holat tarixi ochiladi."
        actions={
          <Link to="/tasks" className="btn btn-text btn-sm">
            Navbat
            <Icon name="chevronRight" size={14} />
          </Link>
        }
      />

      <Toolbar
        search={search}
        onSearch={setSearch}
        placeholder="1C hujjat raqami yoki ID…"
        onRefresh={() => void history.refetch()}
        refreshing={history.isFetching}
      >
        <SegmentedFilter
          value={scope}
          onChange={setScope}
          options={[
            { value: "COMPLETED", label: "Yakunlangan" },
            { value: "CANCELLED", label: "Bekor qilingan" },
            { value: "ALL", label: "Hammasi" },
          ]}
        />
        <select
          className="input"
          value={type}
          onChange={(event) => setType(event.target.value)}
          aria-label="Topshiriq turi"
        >
          <option value="">Barcha turlar</option>
          {TASK_TYPES.map((value) => (
            <option key={value} value={value}>
              {statusLabel(value)}
            </option>
          ))}
        </select>
        <select
          className="input"
          value={worker}
          onChange={(event) => setWorker(event.target.value)}
          aria-label="Ijrochi"
        >
          <option value="">Barcha xodimlar</option>
          {people.map((user) => (
            <option key={user.id} value={user.id}>
              {user.full_name || user.username}
              {user.is_active ? "" : " (nofaol)"}
            </option>
          ))}
        </select>
        <label className="history-date">
          <span>Dan</span>
          <input
            className="input"
            type="date"
            value={from}
            max={to || undefined}
            onChange={(event) => setFrom(event.target.value)}
          />
        </label>
        <label className="history-date">
          <span>Gacha</span>
          <input
            className="input"
            type="date"
            value={to}
            min={from || undefined}
            onChange={(event) => setTo(event.target.value)}
          />
        </label>
        {filtered && (
          <button
            type="button"
            className="btn btn-text btn-sm"
            onClick={() => {
              setType("");
              setWorker("");
              setFrom("");
              setTo("");
              setSearch("");
            }}
          >
            <Icon name="close" size={14} />
            Tozalash
          </button>
        )}
      </Toolbar>

      <DataTable
        tableId="task-history"
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        isLoading={history.isLoading}
        error={history.error}
        onRetry={() => void history.refetch()}
        emptyTitle={filtered ? "Filtrga mos topshiriq yo'q" : "Tarix bo'sh"}
        emptyHint={
          filtered
            ? "Davr, tur yoki xodimni o'zgartiring."
            : "Topshiriq yakunlangan yoki bekor qilingan zahoti shu yerda ko'rinadi."
        }
        hasMore={history.data?.has_more}
        onRowClick={setOpened}
        activeKey={opened?.id}
        footer={
          <Pager
            page={pager.page}
            atFirst={pager.atFirst}
            shown={rows.length}
            hasMore={history.data?.has_more}
            onPrev={pager.prev}
            onNext={() => pager.next(history.data?.next_cursor)}
          />
        }
      />

      {opened && (
        <TaskDrawer key={opened.id} task={opened} onClose={() => setOpened(null)} />
      )}
    </>
  );
}

/**
 * Farq belgisi: kamomad satri, reja bilan mos kelmagan fakt yoki
 * «mos». Bekor qilingan ishda farq ma'nosiz — u bajarilmagan.
 */
function DiscrepancyBadge({ row }: { row: FinishedTask }) {
  if (row.status === "CANCELLED" || row.line_count === 0) return <>—</>;
  if (row.short_lines > 0) {
    return <StatusBadge value="SHORTAGE" label={`Kamomad · ${row.short_lines}`} />;
  }
  if (row.mismatch_lines > 0) {
    return <StatusBadge value="MISMATCH" tone="warning" label={`Farq · ${row.mismatch_lines}`} />;
  }
  return <StatusBadge value="MATCH" label="Mos" />;
}

/**
 * Tafsilot: satrlar (tovar, reja, fakt, joy) va holat tarixi.
 *
 * Ro'yxat javobida satrlar YO'Q — topshiriq alohida so'raladi.
 * Tovar va katak nomlari ham BITTA so'rovda (`ids`).
 */
function TaskDrawer({ task, onClose }: { task: FinishedTask; onClose: () => void }) {
  const detail = useItem<TaskDetail>("task", `/api/v1/tasks/${task.id}`);
  const history = useList<HistoryEntry>("task", `/api/v1/tasks/${task.id}/history`, { limit: 200 });

  const lines = useMemo(
    () => [...(detail.data?.lines ?? [])].sort((left, right) => left.line_number - right.line_number),
    [detail.data],
  );
  const entries = history.data?.items ?? [];

  const skuIds = [...new Set(lines.map((line) => line.sku_id))].join(",");
  const locationIds = [
    ...new Set(
      lines.flatMap((line) => [line.source_location_id, line.target_location_id]).filter(Boolean),
    ),
  ].join(",");
  const skus = useList<{ id: string; article: string; name: string; pack_format?: string }>(
    "masterData", "/api/v1/skus", { ids: skuIds, limit: 500 }, { enabled: skuIds !== "" });
  const locations = useList<{ id: string; code: string }>(
    "topology", "/api/v1/locations", { ids: locationIds, limit: 500 }, { enabled: locationIds !== "" });
  const sku = new Map((skus.data?.items ?? []).map((item) => [item.id, item]));
  const cell = new Map((locations.data?.items ?? []).map((item) => [item.id, item.code]));
  const cellCode = (id: string | undefined) => (id ? cell.get(id) ?? id.slice(0, 8) : "—");

  const names = usePeople([
    task.assignee_id,
    ...entries.map((entry) => entry.actor_id),
    ...lines.map((line) => line.confirmed_by),
  ]);

  const seconds = workSeconds(task);

  return (
    <Drawer label="Topshiriq tafsiloti">
      <div className="drawer-head">
        <div>
          <div className="dialog-title">
            {task.document_number ?? `Topshiriq #${shortTaskId(task.id)}`}
          </div>
          <div className="dialog-desc">
            {statusLabel(task.task_type)} · <StatusBadge value={task.status} /> ·{" "}
            {personName(names, task.assignee_id)}
          </div>
        </div>
        <button type="button" className="btn btn-icon" onClick={onClose} aria-label="Yopish">
          <Icon name="close" />
        </button>
      </div>

      <div className="drawer-body">
        <dl className="kv">
          <dt>Ijrochi</dt>
          <dd>
            {task.assignee_id
              ? personName(names, task.assignee_id)
              : `Hech kim olmagan (${roleLabel(task.required_role)})`}
          </dd>
          <dt>Yaratildi</dt>
          <dd>{dateTime(task.created_at)}</dd>
          <dt>Boshlandi</dt>
          <dd>{dateTime(task.started_at ?? task.accepted_at)}</dd>
          <dt>{task.status === "CANCELLED" ? "Bekor qilindi" : "Yakunlandi"}</dt>
          <dd>{dateTime(task.finished_at)}</dd>
          <dt>Davomiyligi</dt>
          <dd>{duration(seconds)}</dd>
          <dt>Reja / fakt</dt>
          <dd className="num">
            {qty(task.planned_qty)} / {qty(task.actual_qty)} · {task.line_count} SKU
          </dd>
          {task.source_process && (
            <>
              <dt>Manba jarayon</dt>
              <dd>{statusLabel(task.source_process)}</dd>
            </>
          )}
          {task.reason && (
            <>
              <dt>Sabab</dt>
              <dd>{task.reason}</dd>
            </>
          )}
          <dt>ID</dt>
          <dd className="mono">{task.id}</dd>
        </dl>

        <section>
          <div className="field-label" style={{ marginBottom: 6 }}>
            Satrlar
          </div>
          {detail.isLoading ? (
            <div className="card skeleton" style={{ height: 120 }} />
          ) : detail.error ? (
            <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
          ) : lines.length === 0 ? (
            <div className="cell-sub">Topshiriqda satr yo'q.</div>
          ) : (
            <table className="data">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Tovar</th>
                  <th className="right">Reja</th>
                  <th className="right">Fakt</th>
                  <th>Holat</th>
                  <th>Joy</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => {
                  const item = sku.get(line.sku_id);
                  const short =
                    line.actual_qty !== undefined &&
                    line.actual_qty !== "" &&
                    Number(line.actual_qty) < Number(line.planned_qty);
                  return (
                    <tr key={line.id}>
                      <td className="num">{line.line_number}</td>
                      <td>
                        {item ? (
                          <>
                            <span className="mono">{item.article}</span> · {item.name}
                            {item.pack_format && (
                              <div className="cell-sub">
                                Qadoq: <span className="mono">{item.pack_format}</span>
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="mono">{line.sku_id.slice(0, 8)}</span>
                        )}
                        {line.discrepancy_reason && (
                          <div className="cell-sub">{line.discrepancy_reason}</div>
                        )}
                      </td>
                      <td className="num right">{qty(line.planned_qty)}</td>
                      <td
                        className="num right"
                        style={short ? { color: "var(--error)", fontWeight: 600 } : undefined}
                      >
                        {qty(line.actual_qty)}
                      </td>
                      <td>
                        {/* SHORT lug'atda neytral («Kam») — tarixda u
                            kamomad belgisi, ko'zga tashlanishi kerak. */}
                        <StatusBadge
                          value={line.status}
                          tone={line.status === "SHORT" ? "warning" : undefined}
                        />
                      </td>
                      <td className="mono">
                        {line.source_location_id || line.target_location_id
                          ? `${cellCode(line.source_location_id)} → ${cellCode(line.target_location_id)}`
                          : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>

        <section>
          <div className="field-label" style={{ marginBottom: 6 }}>
            Holat tarixi
          </div>
          {history.isLoading ? (
            <div className="card skeleton" style={{ height: 80 }} />
          ) : history.error ? (
            <ErrorState error={history.error} onRetry={() => void history.refetch()} />
          ) : entries.length === 0 ? (
            <div className="cell-sub">Holat o'zgarishi qayd etilmagan.</div>
          ) : (
            <table className="data">
              <thead>
                <tr>
                  <th>Vaqt</th>
                  <th>O'tish</th>
                  <th>Kim</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, index) => (
                  <tr key={`${entry.occurred_at}-${index}`}>
                    <td className="num">{dateTime(entry.occurred_at)}</td>
                    <td>
                      {entry.from_status && (
                        <>
                          <StatusBadge value={entry.from_status} /> →{" "}
                        </>
                      )}
                      <StatusBadge value={entry.to_status} />
                      {entry.reason && <div className="cell-sub">{entry.reason}</div>}
                    </td>
                    <td>
                      {entry.actor_id ? personName(names, entry.actor_id) : "Tizim"}
                      {entry.device_id && <div className="cell-sub">{text(entry.device_id)}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </Drawer>
  );
}
